import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT, COLORS, SKILLS, SkillId } from '../config';
import { SkillManager } from '../managers/SkillManager';
import { GameScene } from '../scenes/GameScene';
import { formatNumber } from '../utils/formatNumber';

const SKILL_IDS: SkillId[] = ['poison_arrows', 'rock_rain', 'gold_rush', 'lightning_strike'];

// Skill icon colors
const SKILL_COLORS: Record<SkillId, number> = {
  poison_arrows: 0x44cc44,
  rock_rain: 0x888888,
  gold_rush: 0xffcc00,
  lightning_strike: 0xffff00,
};

interface SkillButton {
  id: SkillId;
  bg: Phaser.GameObjects.Graphics;
  iconGfx: Phaser.GameObjects.Graphics;
  cooldownGfx: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
  hitArea: Phaser.GameObjects.Rectangle;
  x: number;
  y: number;
}

export class SkillBar {
  private scene: Phaser.Scene;
  private buttons: SkillButton[] = [];
  private tooltipBg: Phaser.GameObjects.Graphics | null = null;
  private tooltipText: Phaser.GameObjects.Text | null = null;
  private gameScene: GameScene;

  private readonly ICON_SIZE = 36;
  private readonly GAP = 8;
  private readonly BAR_Y: number;

  constructor(scene: Phaser.Scene, gameScene: GameScene) {
    this.scene = scene;
    this.gameScene = gameScene;
    // Position above the shop panel (which is 70px from bottom)
    this.BAR_Y = GAME_HEIGHT - 70 - this.ICON_SIZE - 10;

    this.createButtons();
  }

  private createButtons(): void {
    for (let i = 0; i < SKILL_IDS.length; i++) {
      const id = SKILL_IDS[i];
      const x = 0;
      const y = this.BAR_Y;

      const bg = this.scene.add.graphics();
      bg.setDepth(900);
      const iconGfx = this.scene.add.graphics();
      iconGfx.setDepth(901);
      const cooldownGfx = this.scene.add.graphics();
      cooldownGfx.setDepth(902);

      const label = this.scene.add.text(x + this.ICON_SIZE / 2, y + this.ICON_SIZE + 2, '', {
        fontSize: '8px',
        fontFamily: 'Arial, sans-serif',
        color: '#aaaaaa',
        align: 'center',
      }).setOrigin(0.5, 0).setDepth(903);

      const hitArea = this.scene.add.rectangle(
        x + this.ICON_SIZE / 2, y + this.ICON_SIZE / 2,
        this.ICON_SIZE, this.ICON_SIZE
      ).setInteractive({ useHandCursor: true }).setAlpha(0.001).setDepth(904);

      hitArea.on('pointerdown', () => {
        this.gameScene.activateSkill(id);
        this.hideTooltip();
      });

      hitArea.on('pointerover', () => {
        this.showTooltip(btn.x + this.ICON_SIZE / 2, btn.y, id);
      });

      hitArea.on('pointerout', () => {
        this.hideTooltip();
      });

      const btn: SkillButton = { id, bg, iconGfx, cooldownGfx, label, hitArea, x, y };
      this.buttons.push(btn);
    }
  }

  update(skillManager: SkillManager): void {
    // Skills temporarily disabled — hide all buttons
    for (const btn of this.buttons) {
      btn.bg.clear();
      btn.iconGfx.clear();
      btn.cooldownGfx.clear();
      btn.label.setVisible(false);
      btn.hitArea.setVisible(false);
    }
    return;

    const ownedIds = SKILL_IDS.filter(id => skillManager.isOwned(id));
    const totalW = ownedIds.length * this.ICON_SIZE + Math.max(0, ownedIds.length - 1) * this.GAP;
    const startX = GAME_WIDTH - totalW - 20;

    let ownedIndex = 0;

    for (const btn of this.buttons) {
      const owned = skillManager.isOwned(btn.id);

      if (!owned) {
        btn.bg.clear();
        btn.iconGfx.clear();
        btn.cooldownGfx.clear();
        btn.label.setText('');
        btn.hitArea.setVisible(false);
        if (btn.hitArea.input) btn.hitArea.input!.enabled = false;
        continue;
      }

      btn.x = startX + ownedIndex * (this.ICON_SIZE + this.GAP);
      btn.y = this.BAR_Y;
      ownedIndex++;

      btn.hitArea.setVisible(true);
      btn.hitArea.setPosition(btn.x + this.ICON_SIZE / 2, btn.y + this.ICON_SIZE / 2);

      const canActivate = skillManager.canActivate(btn.id);
      const cooldownPercent = skillManager.getCooldownPercent(btn.id);

      btn.bg.clear();
      const borderColor = canActivate ? 0xffffff : 0x666666;
      btn.bg.fillStyle(0x1a1a2e, 0.9);
      btn.bg.fillRoundedRect(btn.x, btn.y, this.ICON_SIZE, this.ICON_SIZE, 4);
      btn.bg.lineStyle(2, borderColor, canActivate ? 0.8 : 0.4);
      btn.bg.strokeRoundedRect(btn.x, btn.y, this.ICON_SIZE, this.ICON_SIZE, 4);

      btn.iconGfx.clear();
      this.drawSkillIcon(btn.iconGfx, btn.x, btn.y, btn.id, canActivate);

      btn.cooldownGfx.clear();
      if (cooldownPercent > 0) {
        btn.cooldownGfx.fillStyle(0x000000, 0.6);
        const h = this.ICON_SIZE * cooldownPercent;
        btn.cooldownGfx.fillRoundedRect(btn.x, btn.y, this.ICON_SIZE, h, { tl: 4, tr: 4, bl: 0, br: 0 });
      }

      const config = SKILLS[btn.id];
      btn.label.setText(config.name.split(' ')[0]);
      btn.label.setPosition(btn.x + this.ICON_SIZE / 2, btn.y + this.ICON_SIZE + 2);

      if (btn.hitArea.input) {
        btn.hitArea.input!.enabled = true;
        btn.hitArea.input!.cursor = canActivate ? 'pointer' : 'default';
      }
    }
  }

  private drawSkillIcon(gfx: Phaser.GameObjects.Graphics, x: number, y: number, id: SkillId, active: boolean): void {
    const cx = x + this.ICON_SIZE / 2;
    const cy = y + this.ICON_SIZE / 2;
    const color = SKILL_COLORS[id];
    const alpha = active ? 1 : 0.4;

    switch (id) {
      case 'poison_arrows':
        // Green arrows raining down
        gfx.lineStyle(1.5, color, alpha);
        for (let i = 0; i < 3; i++) {
          const ax = cx - 6 + i * 6;
          const ay = cy - 8 + i * 5;
          gfx.beginPath();
          gfx.moveTo(ax, ay - 6);
          gfx.lineTo(ax, ay + 4);
          gfx.strokePath();
          gfx.fillStyle(color, alpha);
          gfx.fillTriangle(ax - 2, ay + 3, ax + 2, ay + 3, ax, ay + 7);
        }
        // Poison drops
        gfx.fillStyle(0x22aa22, alpha * 0.6);
        gfx.fillCircle(cx - 4, cy + 8, 2);
        gfx.fillCircle(cx + 5, cy + 6, 1.5);
        break;

      case 'rock_rain':
        // Falling rocks
        gfx.fillStyle(color, alpha);
        gfx.fillCircle(cx - 6, cy - 4, 4);
        gfx.fillCircle(cx + 5, cy - 8, 3);
        gfx.fillCircle(cx, cy + 4, 5);
        gfx.fillCircle(cx + 7, cy + 2, 3);
        gfx.lineStyle(1, 0xaaaaaa, alpha * 0.6);
        gfx.beginPath();
        gfx.moveTo(cx - 8, cy - 12); gfx.lineTo(cx - 10, cy - 6);
        gfx.moveTo(cx + 2, cy - 14); gfx.lineTo(cx, cy - 8);
        gfx.moveTo(cx + 9, cy - 10); gfx.lineTo(cx + 7, cy - 4);
        gfx.strokePath();
        break;

      case 'gold_rush':
        // Gold coins
        gfx.fillStyle(color, alpha);
        gfx.fillCircle(cx, cy - 4, 6);
        gfx.fillCircle(cx - 5, cy + 5, 5);
        gfx.fillCircle(cx + 6, cy + 3, 4);
        // Coin detail
        gfx.lineStyle(1, 0xaa8800, alpha * 0.8);
        gfx.strokeCircle(cx, cy - 4, 4);
        gfx.strokeCircle(cx - 5, cy + 5, 3);
        break;

      case 'lightning_strike':
        // Lightning bolt
        gfx.fillStyle(color, alpha);
        gfx.fillTriangle(cx - 2, cy - 12, cx + 6, cy - 12, cx, cy - 2);
        gfx.fillTriangle(cx - 6, cy + 12, cx + 2, cy - 2, cx - 2, cy - 2);
        gfx.fillTriangle(cx - 6, cy + 12, cx + 2, cy + 2, cx + 2, cy - 2);
        break;
    }
  }

  private showTooltip(x: number, y: number, id: SkillId): void {
    this.hideTooltip();

    const config = SKILLS[id];
    const sm = this.gameScene.skillManager;

    let text = `${config.name}\n${config.description}`;
    const level = sm.getLevel(id);
    text += `\nLevel: ${level}`;

    switch (id) {
      case 'poison_arrows':
        text += `\nPoison DPS: ${formatNumber(sm.getSkillValue(id, 'damage'))}`;
        text += `\nDuration: ${(sm.getSkillValue(id, 'duration') / 1000).toFixed(1)}s`;
        text += `\nHits all enemies`;
        break;
      case 'rock_rain':
        text += `\nDamage: ${formatNumber(sm.getSkillValue(id, 'damage'))}`;
        text += `\nRadius: ${sm.getSkillValue(id, 'radius')}px`;
        text += `\nClick to target area`;
        break;
      case 'gold_rush':
        text += `\nGold: ${sm.getSkillValue(id, 'multiplier')}x multiplier`;
        text += `\nDuration: ${(sm.getSkillValue(id, 'duration') / 1000).toFixed(0)}s`;
        break;
      case 'lightning_strike':
        text += `\nDamage: ${formatNumber(sm.getSkillValue(id, 'damage'))}`;
        text += `\nChains: ${sm.getSkillValue(id, 'chains')}`;
        break;
    }

    const cooldown = Math.max(config.baseCooldown - (level - 1) * config.cooldownReduction, 3000);
    text += `\nCooldown: ${(cooldown / 1000).toFixed(0)}s`;

    const lines = text.split('\n');
    const lineHeight = 14;
    const padding = 8;
    const width = 170;
    const height = lines.length * lineHeight + padding * 2;

    let tooltipX = x - width / 2;
    tooltipX = Math.max(10, Math.min(GAME_WIDTH - width - 10, tooltipX));
    const tooltipY = y - height - 8;

    this.tooltipBg = this.scene.add.graphics();
    this.tooltipBg.fillStyle(0x1a1a2e, 0.95);
    this.tooltipBg.fillRoundedRect(tooltipX, tooltipY, width, height, 6);
    this.tooltipBg.lineStyle(1, 0x4488ff, 0.5);
    this.tooltipBg.strokeRoundedRect(tooltipX, tooltipY, width, height, 6);
    this.tooltipBg.setDepth(1000);

    this.tooltipText = this.scene.add.text(tooltipX + padding, tooltipY + padding, text, {
      fontSize: '11px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      lineSpacing: 2,
    }).setDepth(1001);
  }

  private hideTooltip(): void {
    if (this.tooltipBg) { this.tooltipBg.destroy(); this.tooltipBg = null; }
    if (this.tooltipText) { this.tooltipText.destroy(); this.tooltipText = null; }
  }
}
